(ns ductile.ui
  ;; A namespace must be required below if it is to be part of the
  ;; UI. It is irrelevant whether the other ns is used in this ns.
  (:require ["@bugsnag/js" :as bugsnag]
            ["react" :as react]
            ["react-dom/client" :as react-client]
            [applied-science.js-interop :as j]
            [clojure.string :as str]
            [ductile.time.format :as time-format]
            [ductile.ui.accessories]
            [ductile.ui.agent]
            [ductile.ui.announcements]
            [ductile.ui.auth]
            [ductile.ui.billing]
            [ductile.ui.carrier-billing]
            [ductile.ui.commands :as commands]
            [ductile.ui.components :as ui]
            [ductile.ui.components.columns]
            [ductile.ui.components.waypoints]
            [ductile.ui.compound]
            [ductile.ui.contract]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.edi]
            [ductile.ui.elog]
            [ductile.ui.elog.interface]
            [ductile.ui.entity]
            [ductile.ui.gate]
            [ductile.ui.http :as http]
            [ductile.ui.invoices]
            [ductile.ui.labels]
            [ductile.ui.load-builder]
            [ductile.ui.load-builder-v2]
            [ductile.ui.locations]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.nexus]
            [ductile.ui.report-damages]
            [ductile.ui.scanner :as scanner]
            [ductile.ui.scene.viewer]
            [ductile.ui.schema-based-editor]
            [ductile.ui.scope]
            [ductile.ui.service-list]
            [ductile.ui.transport]
            [ductile.ui.truck-booking]
            [ductile.ui.users]
            [ductile.ui.vehicle-call-off]
            [ductile.ui.vehicle-search]
            [ductile.ui.view-stack]
            [ductile.ui.vin-list]
            [ductile.ui.workflow :as workflow]
            [ductile.ui.workflow-generator]
            [ductile.ui.workflow-visualization]
            [ductile.util :as util :refer [if-ok-let]]
            [lambdaisland.glogi :as log]
            [lambdaisland.glogi.console :as glogi-console]
            [nextjournal.clerk.render :as render]
            [nextjournal.clerk.render.context :as view-context]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.viewer :as viewer]
            [reagent.core :as r]
            [shadow.cljs.modern :refer [defclass]])
  (:require-macros [ductile.ui.macros :refer [build-info defc]]
                   [ductile.ui.resolver :refer [sym->view-fn]]))

(defn resolve-view-fn [sym]
  (let [sym->fn (sym->view-fn)]
    (or (sym->fn sym)
        {:error ::resolve-error
         :error-message (str "Unable to resolve `" sym "`." {:sym sym :available-symbols (keys sym->fn)})})))

(glogi-console/install!)

(def build-stamp (build-info))

(defn stage []
  (if (exists? js/location)
    (case js/location.host
      "ars-demo.ductile.de"    "demo"
      "ars-staging.ductile.de" "staging"
      "ars.ductile.de"         "production"
      "development")
    "development"))

(bugsnag/start (clj->js
                {:apiKey "21c73df723637d5d2c4a569a78c1fbbc"
                 :enabledReleaseStages ["production" "staging" "demo"]
                 :releaseStage (stage)
                 :appVersion (:sha build-stamp)
                 :onError (fn [event]
                            (.addMetadata event "ex-data" (clj->js (ex-data (.-originalError event))))
                            (.addMetadata event "screen" #js {:screenWidth (.-width js/screen)
                                                              :screenHeight (.-height js/screen)
                                                              :screenAvailWidth (.-availWidth js/screen)
                                                              :screenAvailHeight (.-availHeight js/screen)
                                                              :devicePixelRatio js/devicePixelRatio
                                                              :innerWidth js/innerWidth
                                                              :innerHeight js/innerHeight})
                            (if-let [user @deframe/!current-user]
                              (.setUser event (str (:ductile/id user)) (:user/email user) "")
                              (.setUser event nil "not logged in" "")))}))

#_[:off
   :finest
   :trace
   :debug
   :config
   :info
   :warn
   :error
   :shout
   :all]

(goog-define log-level "info")

(log/set-levels
 {:glogi/root (keyword log-level)
  'goog.net.XhrIo :warn})
;; See all dispatch/subscribe events
;;re-frame.router :trace
;;re-frame.subs   :trace


;; Routing

(defn fields? []
  (seq @ui/!field-state))

(def commands-config
  {:categories
   [:vin
    :nav
    :dev
    :command-bar
    :nextjournal.commands.views]
   :shortcuts
   {:default/actions {:commands [:vin/lookup :reservations/lookup]}
    :field/actions {:commands [:field/edit] :requires #{fields?}}}})

(defclass ErrorBoundary
  (extends react/Component)
  (field componentDidCatch)
  (field hash)
  (constructor [this ^js props]
               (super props)
               (set! (.-state this) #js {:error nil :hash (j/get props :hash)})
               (set! hash (j/get props :hash)))

  Object
  (componentDidCatch [this error info]
                     (bugsnag/notify error))
  (render [this ^js props]
          (j/let [^js {{:keys [error]} :state
                       {:keys [children]} :props} this]
            (if error
              (r/as-element [ui/error-view (merge {:error :ductile.ui/app-error-boundary
                                                   :error-message (ex-message error)
                                                   :stack (.-stack error)}
                                                  (ex-data error))])
              children))))

(j/!set ErrorBoundary
        :getDerivedStateFromError (fn [error] #js {:error error})
        :getDerivedStateFromProps (fn [props state]
                                    (when (not= (j/get props :hash)
                                                (j/get state :hash))
                                      #js {:hash (j/get props :hash) :error nil})))

(defn inspect-with-fetch-fn [presented]
  (let [!presented-value (hooks/use-state presented)]
    [view-context/provide {:fetch-fn
                           (fn [elision]
                             (deframe/call-route-refetch
                              {:on-result (fn [more] (swap! !presented-value viewer/merge-presentations more elision))
                               :extra-params {:nextjournal.clerk/elision elision}}))}
     [render/inspect-presented @!presented-value]]))

(defn inspect-dwim [data]
  (if (and (viewer/wrapped-value? data) (contains? data :path))
    (if (contains? data :elisions)
      [inspect-with-fetch-fn data]
      [render/inspect-presented data])
    [render/inspect data]))


(defn render-route-view [match]
  (let [skip-loading-spinner? (-> match :data :skip-loading-spinner?)
        {:keys [loading error result]} @deframe/!current-route-fetch]
    (cond
      (or result (and (or skip-loading-spinner? (not loading)) (not error))) ; result may be nil, error may be expected
      (if-let [view (some-> match :data :view)]
        (if-ok-let [view-fn (resolve-view-fn view)]
          [view-fn result (cond-> match
                            skip-loading-spinner? (assoc :loading loading))]
          [ui/error-view view-fn])
        ^{:key (hash result)} [inspect-dwim (render/eval-cljs-evals result)])

      loading ; needs to be after the result case to prevent jumps to top
      [:div.bg-white.md:bg-gray-200.min-h-screen
       [ui/loading-spinner]]

      error
      [ui/error-view error])))

(defn popup-layout [match]
  [:div.w-full.relative.min-h-screen.h-full
   [:div.h-full.w-full.left-0.top-0.absolute.overflow-auto
    [render-route-view match]]])

(defc mobile-layout [match]
  [:<>
   [mobile/scroll-content
    (when (http/speculative-transactions?)
      [ui/speculative-transactions-indicator @http/!speculative-transaction-opts])
    [render-route-view match]]
   [workflow/view-stack-controller {:mobile? true}]])

(defn desktop-layout [match]
  (let [popup-window? (= "1" (.get (js/URLSearchParams. (.-search js/location)) "popup"))]
    [ui/page
     (when-let [speculative-opts @http/!speculative-transaction-opts]
       [ui/slot {:container :site-wide-indicator}
        [ui/speculative-transactions-indicator speculative-opts]])
     [:div.print:hidden
      [ui/toast-provider
       (when-let [toast @(deframe/!get :toast)]
         [ui/toast toast])]]
     (when-not popup-window?
       ^{:key (:path @deframe/!current-route)}
       [ui/nav-bar])
     [:div
      {:class (if popup-window?
                "p-0 print:p-0"
                "p-5 print:p-0")}
      [render-route-view match]]
     [workflow/view-stack-controller {:mobile? false}]]))

(defn layout-view [match]
  [desktop-layout match])

(defn plain-layout [match]
  [:<>
   (when (http/speculative-transactions?)
     [ui/speculative-transactions-indicator @http/!speculative-transaction-opts])
   [render-route-view match]])

(defn layout [{:as match :keys [path data]}]
  (cond
    (not (:layout data))
    plain-layout

    (or
     (str/starts-with? path "/transport-ops/")
     (str/starts-with? path "/report-damages")
     (str/starts-with? path "/route-plan/")
     (str/starts-with? path "/compound-ops/"))
    mobile-layout

    (str/ends-with? path "/popup-map")
    popup-layout

    :else
    desktop-layout))

#_(layout @deframe/!current-route)

(defn app [{:as _opts}]
  (r/with-let [_ (r/track! (fn []
                             (when-let [current-scan @deframe/!current-scan]
                               (if-let [{:keys [on-scan]} @deframe/!scan-tracker]
                                 (on-scan current-scan)
                                 (workflow/fahrzeug-scan-navigate current-scan)))))]
    [:> ErrorBoundary
     [:<>
      [scanner/scan-confirmation-view]
      [(or (layout @deframe/!current-route) render-route-view)
       @deframe/!current-route]
      [commands/menu]]]))

(defn update-print-css-timestamp! []
  (let [formatted (time-format/->str {} (js/Date.))]
    (.. (js/document.querySelector ":root") -style (setProperty "--current-datetime-formatted" (str "'" formatted "'")))))

(defonce ^js react-root
  (when-let [root-el (and (exists? js/document)
                          (js/document.getElementById "app"))]
    (js/setInterval update-print-css-timestamp! 1000)
    (react-client/createRoot root-el)))

(defn ^:dev/after-load main [opts]
  (when react-root
    (.render react-root (r/as-element [app opts]))))

(defn rerender
  "Called from ductile.ui.macros/defc after top level eval of ui component definition"
  []
  (when-not (.-rerendering js/window)
    (set! (.-rerendering js/window) true)
    (.render react-root (r/as-element [:<>]))
    (js/setTimeout (fn []
                     (main {})
                     (set! (.-rerendering js/window) false))
                   10)))

(defn attempt-reload
  "Event that the server can send the client to trigger a reload after an
  update. The client can choose to delay it and perform it at a later
  time unless the `:force` param is sent."
  {::deframe/event true}
  [_ctx params]
  (let [reload-now? (or (:force params)
                        (not @workflow/!selected-service))]
    (prn ::queue-reload params :reload-now? reload-now?)
    (if reload-now?
      (.reload js/window.location)
      (deframe/assoc! ::reload-params params))))
